import { ChartContainer, ChartTooltip } from "@/components/chart";
import { dailyLabelFormatter, hourlyLabelFormatter } from "@/components/chart-template";

import { useRef } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { AnalysisChartGranularity } from ".";

type PerformanceChartProps = {
  data: Array<Record<string, any>>;
  yAxisKey: string;
  granularity: AnalysisChartGranularity;
  baseDimensionValues: string[];
  sortedDimensionValues?: string[];
  selectedDimensions?: Set<string>;
  overlayMode?: "none" | "ap" | "secondary";
  showOverlay?: boolean;
};

function Chart({
  data,
  yAxisKey,
  granularity,
  baseDimensionValues,
  sortedDimensionValues,
  selectedDimensions,
  overlayMode = "none",
  showOverlay = false,
}: PerformanceChartProps) {
  const ref = useRef<HTMLDivElement>(null);

  const colorMapping: Record<string, number> = {};
  if (sortedDimensionValues && sortedDimensionValues.length > 0) {
    sortedDimensionValues.forEach((value, index) => {
      colorMapping[value] = index + 1;
    });
    let nextColorIndex = sortedDimensionValues.length + 1;
    baseDimensionValues.forEach(value => {
      if (!colorMapping[value]) {
        colorMapping[value] = nextColorIndex++;
      }
    });
  }

  const labelFormatter = (value: string) =>
    granularity === "hourly" ? hourlyLabelFormatter(value) : dailyLabelFormatter(value);

  // Pre-compute ticks: for hourly granularity keep only the first timestamp of each UTC day
  const xTicks =
    granularity === "hourly"
      ? (() => {
          const seen = new Set<string>();
          return chartData[granularity][dimension][metric]
            .map(r => {
              const dt = new Date(r.dateTime);
              const key = `${dt.getUTCFullYear()}-${dt.getUTCMonth()}-${dt.getUTCDate()}`;
              if (!seen.has(key)) {
                seen.add(key);
                return r.dateTime;
              }
              return null;
            })
            .filter(Boolean) as string[];
        })()
      : undefined;

  // Formatter: with filtered ticks we can always show the day label
  const xTickFormatter = (value: string) => dailyLabelFormatter(value);

  const formatter = Intl.NumberFormat("en", { notation: "compact" });
  const pctFormatter = Intl.NumberFormat("en", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload || payload.length === 0) return null;

    const grouped: Record<string, { base?: number; overlay?: number }> = {};

    payload.forEach(item => {
      const key: string = item.dataKey || "";
      const isOverlay = key.endsWith("__ov");
      const baseKey = isOverlay ? key.replace(/__ov$/, "") : key;
      if (!grouped[baseKey]) grouped[baseKey] = {};
      if (isOverlay) grouped[baseKey].overlay = item.value as number;
      else grouped[baseKey].base = item.value as number;
    });

    const ordered = baseDimensionValues.filter(k => grouped[k]);
    const totalBase = ordered.reduce((acc, dim) => acc + (grouped[dim].base || 0), 0);
    const totalOverlay = showOverlay ? ordered.reduce((acc, dim) => acc + (grouped[dim].overlay || 0), 0) : 0;

    return (
      <div className="grid min-w-[10rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
        <div className="font-medium">
          {payload[0]?.payload?.dateTime ? labelFormatter(payload[0].payload.dateTime) : ""}
        </div>
        <div className="grid gap-1">
          {ordered.map(dim => {
            const colorIndex = colorMapping[dim]
              ? colorMapping[dim] % 20 || 20
              : (baseDimensionValues.indexOf(dim) % 20) + 1;
            const entry = grouped[dim];
            const sharePct = entry.base !== undefined && totalBase > 0 ? (entry.base / totalBase) * 100 : 0;
            const overlaySharePct =
              showOverlay && entry.overlay !== undefined && totalOverlay > 0 ? (entry.overlay / totalOverlay) * 100 : 0;
            return (
              <div key={dim} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: `hsl(var(--chart-${colorIndex}))` }}
                  />
                  <span className="text-muted-foreground">{dim}</span>
                </div>
                <div className="flex items-center gap-1 tabular-nums font-mono">
                  {entry.base !== undefined && (
                    <>
                      <span className="font-medium">{entry.base.toLocaleString()}</span>
                      <span className="text-[10px] text-muted-foreground">({pctFormatter.format(sharePct)}%)</span>
                    </>
                  )}
                  {showOverlay && entry.overlay !== undefined && (
                    <>
                      <span className="text-[10px] leading-none px-1 py-0.5 rounded bg-secondary font-semibold">
                        {entry.overlay.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        ({pctFormatter.format(overlaySharePct)}%)
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <ChartContainer config={{}} ref={ref}>
      <LineChart accessibilityLayer data={data}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="dateTime"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          ticks={xTicks}
          tickFormatter={xTickFormatter}
        />
        <YAxis
          tickFormatter={(value: number) => formatter.format(value)}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
        />
        {showOverlay && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tickFormatter={(value: number) => formatter.format(value)}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
        )}
        <ChartTooltip cursor={false} content={<CustomTooltip />} />

        {baseDimensionValues.map(dimensionValue => {
          const colorIndex = colorMapping[dimensionValue]
            ? colorMapping[dimensionValue] % 20 || 20
            : (baseDimensionValues.indexOf(dimensionValue) % 20) + 1;

          const isFiltered =
            selectedDimensions && selectedDimensions.size > 0 && !selectedDimensions.has(dimensionValue);
          if (isFiltered) return null;

          return (
            <Line
              key={dimensionValue + yAxisKey}
              dataKey={dimensionValue}
              type="linear"
              stroke={`hsl(var(--chart-${colorIndex}))`}
              strokeWidth={2}
              dot={false}
            />
          );
        })}

        {showOverlay &&
          baseDimensionValues.map(dimensionValue => {
            const isFiltered =
              selectedDimensions && selectedDimensions.size > 0 && !selectedDimensions.has(dimensionValue);
            if (isFiltered) return null;
            const colorIndex = colorMapping[dimensionValue]
              ? colorMapping[dimensionValue] % 20 || 20
              : (baseDimensionValues.indexOf(dimensionValue) % 20) + 1;
            return (
              <Line
                key={dimensionValue + yAxisKey + "__ov"}
                dataKey={`${dimensionValue}__ov`}
                type="linear"
                stroke={`hsl(var(--chart-${colorIndex}))`}
                strokeWidth={1.75}
                strokeDasharray="4 3"
                dot={false}
                yAxisId="right"
                name={`${dimensionValue} ${overlayMode === "ap" ? "AP" : "2nd AP"}`}
              />
            );
          })}
      </LineChart>
    </ChartContainer>
  );
}

export default Chart;
